import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Award,
  CheckCircle2,
  Leaf,
  Package,
  Phone,
  ShieldCheck,
  Truck,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageBanner from '../components/PageBanner';
import CategoryProductList from '../components/CategoryProductList';
import { fetchAllProducts, fetchProduct } from '../api/catalog';
import { CATALOG } from '../data/productCatalog';
import {
  getCategoryFallbackImage,
  mapApiProductToDetail,
  mapApiProductToWaveItem,
} from '../utils/catalogDisplay';
import { PAGE_BANNER_DEFAULT_IMAGE, PAGE_BANNER_IMAGES } from '../data/homeData';
import '../Home.css';
import '../PageBanner.css';
import '../ProductDetail.css';

function slugify(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function buildFallbackProduct(slug) {
  const match = CATALOG.find((row) => slugify(row.name) === slug);
  if (!match) return null;

  const image = getCategoryFallbackImage(match.category);
  return {
    id: `fallback-${slugify(match.name)}`,
    name: match.name,
    slug: slugify(match.name),
    sku: null,
    hsn: match.hsn || null,
    description: match.tagline || `Premium ${match.name} for export`,
    shortDescription: match.tagline || `Premium ${match.name} for export`,
    category: match.category,
    categorySlug: slugify(match.category),
    image,
    gallery: [image],
    topSelling: false,
    inStock: true,
    stockStatus: 'instock',
    weight: null,
    reviewsCount: 0,
    hasVariants: false,
    variants: [],
    relatedProductIds: [],
    additionalInfo: match.hsn ? { hsn_code: match.hsn } : null,
  };
}

function ProductDetailPage() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [activeImage, setActiveImage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    let cancelled = false;

    async function loadProduct() {
      setLoading(true);
      setError('');

      try {
        const data = await fetchProduct(slug);
        if (cancelled) return;

        const mapped = mapApiProductToDetail(data);
        setProduct(mapped);
        setActiveImage(mapped.gallery[0] || mapped.image);

        const categorySlug = data.category?.slug;
        if (categorySlug) {
          const siblings = await fetchAllProducts({ category_slug: categorySlug });
          if (cancelled) return;
          setCategoryProducts(siblings.map(mapApiProductToWaveItem));
        }
      } catch (err) {
        if (!cancelled) {
          const fallback = buildFallbackProduct(slug);
          if (fallback) {
            setProduct(fallback);
            setActiveImage(fallback.gallery[0] || fallback.image);
            setCategoryProducts(
              CATALOG
                .filter((row) => row.category === fallback.category && slugify(row.name) !== slug)
                .slice(0, 8)
                .map((row, index) => ({
                  id: `fallback-sib-${index}-${slugify(row.name)}`,
                  name: row.name,
                  slug: slugify(row.name),
                  description: row.tagline || `Premium ${row.name} for export`,
                  image: getCategoryFallbackImage(row.category),
                  hsn: row.hsn || null,
                  topSelling: false,
                })),
            );
            setError('');
          } else {
            setError(err.message || 'Product not found.');
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadProduct();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="page">
        <Header />
        <main className="main products-page-main">
          <PageBanner
            image={PAGE_BANNER_IMAGES.products}
            eyebrow="Products"
            title="Loading product…"
            breadcrumbs={[
              { label: 'Products', to: '/products' },
              { label: 'Product' },
            ]}
          />
          <div className="pd-shell">
            <p className="product-status">Loading product details…</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="page">
        <Header />
        <main className="main products-page-main">
          <PageBanner
            image={PAGE_BANNER_IMAGES.products}
            eyebrow="Products"
            title="Product not found"
            lead={error || 'This product may have been removed or is unavailable.'}
            breadcrumbs={[
              { label: 'Products', to: '/products' },
              { label: 'Not found' },
            ]}
          />
          <div className="pd-shell pd-not-found">
            <Link to="/products" className="pd-back-link">
              <ArrowLeft size={16} />
              Back to products
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const specs = [
    { label: 'SKU', value: product.sku },
    { label: 'HSN Code', value: product.hsn },
    { label: 'Category', value: product.category },
    { label: 'Availability', value: product.inStock ? 'In stock' : 'Out of stock' },
    product.weight ? { label: 'Weight', value: product.weight } : null,
  ].filter(Boolean);

  return (
    <div className="page">
      <Header />
      <main className="main products-page-main">
        <PageBanner
          image={product.image || PAGE_BANNER_DEFAULT_IMAGE}
          eyebrow={product.category}
          title={product.name}
          lead={product.tagline || product.description}
          breadcrumbs={[
            { label: 'Products', to: '/products' },
            { label: product.category, to: `/products/category/${product.categorySlug}` },
            { label: product.name },
          ]}
        />

        <section className="pd-hero">
          <div className="pd-shell">
            <div className="pd-grid">
              <div className="pd-gallery reveal">
                <div className="pd-main-image">
                  <img src={activeImage} alt={product.name} />
                  {product.topSelling && (
                    <span className="pd-badge pd-badge--top">
                      <Award size={14} />
                      Top export pick
                    </span>
                  )}
                </div>
                {product.gallery.length > 1 && (
                  <div className="pd-thumbs">
                    {product.gallery.map((url) => (
                      <button
                        key={url}
                        type="button"
                        className={`pd-thumb ${activeImage === url ? 'active' : ''}`}
                        onClick={() => setActiveImage(url)}
                        aria-label="View product image"
                      >
                        <img src={url} alt="" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="pd-info reveal">
                <span className="pd-category">
                  <Leaf size={14} />
                  {product.category}
                </span>
                <h1 className="pd-title">{product.name}</h1>
                <p className="pd-tagline">{product.shortDescription}</p>
                <p className="pd-description-inline">{product.description}</p>

                <div className="pd-status-row">
                  <span className={`pd-stock ${product.inStock ? 'in' : 'out'}`}>
                    <CheckCircle2 size={16} />
                    {product.inStock ? 'Available for export' : 'Currently unavailable'}
                  </span>
                </div>

                <div className="pd-specs">
                  {specs.map((spec) => (
                    <div key={spec.label} className="pd-spec">
                      <span>{spec.label}</span>
                      <strong>{spec.value || '—'}</strong>
                    </div>
                  ))}
                </div>

                <div className="pd-highlights">
                  <article>
                    <ShieldCheck size={20} />
                    <div>
                      <strong>Export quality</strong>
                      <p>FSSAI-ready sourcing with HSN-coded documentation.</p>
                    </div>
                  </article>
                  <article>
                    <Truck size={20} />
                    <div>
                      <strong>Bulk shipping</strong>
                      <p>MOQ from 50 kg with worldwide freight support.</p>
                    </div>
                  </article>
                  <article>
                    <Package size={20} />
                    <div>
                      <strong>Custom packing</strong>
                      <p>Private label and re-labelling on request.</p>
                    </div>
                  </article>
                </div>

                <div className="pd-actions">
                  <Link to="/contact" className="pd-cta-primary">
                    <Phone size={18} />
                    Request bulk quote
                  </Link>
                  <Link
                    to={`/products/category/${product.categorySlug}`}
                    className="pd-cta-secondary"
                  >
                    <ArrowLeft size={16} />
                    All {product.category} products
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {categoryProducts.length > 0 && (
          <CategoryProductList
            products={categoryProducts}
            categoryName={product.category}
            currentSlug={product.slug}
            sectionTitle={`More in ${product.category}`}
          />
        )}
      </main>
      <Footer />
    </div>
  );
}

export default ProductDetailPage;
